import { admittedReportReferenceIndices } from '../../../../lib/reportGeneration/reportEvidenceSourceAdmission';
/**
 * feasibilityBodyHelpers — shared, render-closure-free helpers for the lifted
 * Feasibility body sections (#34 P2 STAGE B).
 *
 * The inline feasibility blocks in `SiteRagReportView` rendered their bodies
 * through render-scoped closures (`renderCitedText`, `appendixVisibleRefIndices`,
 * `blockChrome.scale`). When those bodies move into standalone registry section
 * components (so the modern `NewTypeSectionsRouter` can mount them), the closures
 * must be re-derived from `report`/`theme` ALONE — they are all pure on the
 * report's references/claims and the theme's heading scale. Re-deriving here (vs
 * importing the view) keeps the body DOM BYTE-IDENTICAL to the inline baseline
 * while avoiding a render-tree import cycle.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import type { SiteRagReport } from '../../../../types/siteRag';
import { walkClaimsText } from '../../../../lib/walkClaimsText';
import { computeEffectiveAppendixVisibleRefIndices } from '../../../../lib/reportRendering';
import { roleColor } from '../../../../lib/reportRoleColors';
import { editorialFlat } from '../../../../lib/reportSectionHeading';

/**
 * emptyStateNoteProps — the ONE source for the lifted feasibility study sections'
 * inline "insufficient data" / graceful-omit note (gridStudy / transportStudy /
 * connectivityStudy / riskAssessment / actionPlan).
 *
 * N1.5C-flat (EDITORIAL-MODE ONLY) — when `editorialActive` is ON the note reads
 * as an italic muted note on the cream page behind a 3px accent LEFT-rule (the
 * treatment ported from `sections/SectionShell.tsx`, routed through the shared
 * `editorialFlat()` seam), instead of the grey `bg-surface-2/50`-filled, fully-
 * bordered box. Flag OFF returns the EXACT legacy className + no inline style so
 * the flag-OFF DOM/CSS is byte-identical to the inline-block baseline.
 *
 * Mirrors the identically-named helper in `SiteRagReportView.tsx` (the legacy
 * inline render path) so the lifted registry sections and the inline blocks emit
 * the SAME flatten — keeping the reportSurfaceParity oracle green.
 */
export function emptyStateNoteProps(
  editorialActive: boolean,
  theme: ReportTheme,
  mt: boolean,
): { className: string; style?: React.CSSProperties } {
  if (editorialActive) {
    const flat = editorialFlat(theme.density === 'compact', {
      rule: roleColor(theme, 'rule'),
      accent: roleColor(theme, 'accent'),
      muted: roleColor(theme, 'muted'),
    });
    return {
      className: `text-xs italic px-3 py-2${mt ? ' mt-4' : ''}`,
      style: { ...flat.leftRule, color: roleColor(theme, 'muted') },
    };
  }
  return {
    className: `text-xs text-text-muted italic px-3 py-2 border border-border-subtle bg-surface-2/50${mt ? ' mt-4' : ''}`,
  };
}

/** Clamp a (possibly stale / undefined) heading scale — mirrors the viewer. */
export function resolveBlockScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

/**
 * A `renderCitedText` equivalent to the SiteRagReportView render closure:
 * `walkClaimsText(text, report.references, report.claims, appendixVisibleRefIndices)`.
 * Pure on `report`.
 */
export function makeRenderCitedText(
  report: SiteRagReport,
): (text: string | undefined | null) => React.ReactNode {
  const appendixVisibleRefIndices = admittedReportReferenceIndices(report, computeEffectiveAppendixVisibleRefIndices(
    report.references,
    { reportType: report.reportType },
  ) ?? undefined);
  return (text: string | undefined | null) =>
    walkClaimsText(text, report.references, report.claims, appendixVisibleRefIndices);
}
